import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Search, Link as LinkIcon, Unlink, SplitSquareHorizontal } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function TransactionInvoiceDialog({ transaction, open, onOpenChange, onUpdate }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");
  
  // Split state
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmount, setSplitAmount] = useState("");

  const fetchInvoices = async () => {
    if (!transaction) return;
    setLoading(true);
    try {
      // tx.type == "expense" -> "ALIS" (Bizim aldığımız / Gider)
      // tx.type == "income" -> "SATIS" (Bizim kestiğimiz / Gelir)
      const targetType = transaction.type === "expense" ? "ALIS" : "SATIS";
      const { data } = await axios.get(`${BACKEND_URL}/api/invoices`, {
        params: { type: targetType, sort_by: "date", sort_order: -1 }
      });
      setInvoices(data);
    } catch (err) {
      toast.error("Faturalar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && transaction) {
      fetchInvoices();
      setSplitMode(false);
      setSplitAmount("");
    }
  }, [open, transaction]);

  const handleLink = async (invId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${invId}/link_transaction/${transaction.id}`);
      toast.success("İşlem faturaya eşlendi.");
      onUpdate();
      onOpenChange(false);
    } catch (err) {
      toast.error("Hata oluştu.");
    }
  };

  const handleLinkAndSplit = async (inv) => {
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${inv.id}/link_and_split/${transaction.id}`, {
        split_amount: inv.remaining_amount
      });
      toast.success(`İşlem bölündü ve ${formatCurrency(inv.remaining_amount)} faturaya eşlendi.`);
      onUpdate();
      onOpenChange(false);
    } catch (err) {
      toast.error("Hata oluştu.");
    }
  };
  const handleUnlink = async () => {
    if (!transaction.invoice_id) return;
    try {
      await axios.post(`${BACKEND_URL}/api/invoices/${transaction.invoice_id}/unlink_transaction/${transaction.id}`);
      toast.success("Eşleşme kaldırıldı.");
      onUpdate();
      onOpenChange(false);
    } catch (err) {
      toast.error("Hata oluştu.");
    }
  };

  const handleSplit = async () => {
    const val = parseFloat(splitAmount);
    if (!val || val <= 0 || val >= transaction.amount) {
      toast.error("Lütfen geçerli bir tutar girin.");
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/transactions/${transaction.id}/split`, {
        amount1: transaction.amount - val,
        amount2: val
      });
      toast.success("İşlem başarıyla bölündü.");
      setSplitMode(false);
      onUpdate();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata oluştu.");
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(val);
  const formatDate = (d) => { try { return format(new Date(d), "dd MMM yyyy", { locale: tr }) } catch { return d } };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>İşlemi Faturaya Eşleştir</DialogTitle>
        </DialogHeader>

        <div className="bg-neutral-50 p-4 rounded-md border flex items-center justify-between mt-2">
          <div>
            <p className="text-xs text-neutral-500">Seçili İşlem</p>
            <p className="font-semibold">{formatDate(transaction.date)} - {transaction.account_name || transaction.description}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-neutral-500">Tutar</p>
            <p className="font-bold text-lg">{formatCurrency(transaction.amount)}</p>
          </div>
        </div>

        {transaction.invoice_id ? (
          <div className="flex justify-between items-center bg-green-50 border border-green-200 p-4 rounded-md">
            <div>
              <p className="text-green-800 font-semibold mb-1">Bu işlem zaten bir faturaya eşlenmiş durumda.</p>
              <p className="text-sm text-green-700">Fatura ID: {transaction.invoice_id}</p>
            </div>
            <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={handleUnlink}>
              <Unlink className="w-4 h-4 mr-2" /> Eşleşmeyi Kaldır
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-2">
              <div className="flex gap-2">
                <Button variant={quickFilter === "all" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setQuickFilter("all")}>Tümü</Button>
                <Button variant={quickFilter === "exact" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setQuickFilter("exact")}>Birebir Tutar</Button>
                <Button variant={quickFilter === "date" ? "default" : "outline"} size="sm" className="h-7 text-xs" onClick={() => setQuickFilter("date")}>Aynı Tarih</Button>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" />
                <Input 
                  placeholder="Fatura No veya Tutar..." 
                  className="pl-8 h-8 text-xs" 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                />
              </div>
            </div>

            <div className="border rounded-md max-h-64 overflow-y-auto">
              <Table>
                <TableHeader className="bg-gray-50 sticky top-0">
                  <TableRow>
                    <TableHead>Fatura No</TableHead>
                    <TableHead>Tarih</TableHead>
                    <TableHead>Gönderici Adı</TableHead>
                    <TableHead className="text-right">Toplam Tutar</TableHead>
                    <TableHead className="text-right">Kalan Miktar</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4">Yükleniyor...</TableCell></TableRow>
                  ) : invoices.filter(inv => {
                    if (quickFilter === "exact") return Math.abs(inv.total_amount - transaction.amount) < 0.01 || Math.abs((inv.remaining_amount||0) - transaction.amount) < 0.01;
                    if (quickFilter === "date") return inv.date.substring(0, 10) === transaction.date.substring(0, 10);
                    return true;
                  }).filter(inv => 
                    (inv.invoice_no || "").toLowerCase().includes(search.toLowerCase()) || 
                    (inv.sender_name || inv.account_name || "").toLowerCase().includes(search.toLowerCase()) || 
                    String(inv.total_amount).includes(search)
                  ).length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4 text-gray-500">Uygun fatura bulunamadı.</TableCell></TableRow>
                  ) : (
                    invoices.filter(inv => {
                      if (quickFilter === "exact") return Math.abs(inv.total_amount - transaction.amount) < 0.01 || Math.abs((inv.remaining_amount||0) - transaction.amount) < 0.01;
                      if (quickFilter === "date") return inv.date.substring(0, 10) === transaction.date.substring(0, 10);
                      return true;
                    }).filter(inv => 
                      (inv.invoice_no || "").toLowerCase().includes(search.toLowerCase()) || 
                      (inv.sender_name || inv.account_name || "").toLowerCase().includes(search.toLowerCase()) || 
                      String(inv.total_amount).includes(search)
                    ).map(inv => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-neutral-600">{inv.invoice_no}</TableCell>
                        <TableCell>{formatDate(inv.date)}</TableCell>
                        <TableCell>{inv.sender_name || inv.account_name}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(inv.total_amount)}</TableCell>
                        <TableCell className="text-right text-red-600">{formatCurrency(inv.remaining_amount)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {transaction.amount > (inv.remaining_amount || 0) + 0.01 && (inv.remaining_amount || 0) > 0 ? (
                              <Button variant="outline" size="sm" onClick={() => handleLinkAndSplit(inv)} className="h-7 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100">
                                <SplitSquareHorizontal className="h-3 w-3 mr-1" /> Böl ve Eşle
                              </Button>
                            ) : null}
                            <Button variant="outline" size="sm" onClick={() => handleLink(inv.id)} className="h-7 text-xs">
                              <LinkIcon className="h-3 w-3 mr-1" /> Eşle
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
